<template>
  <div>
    <a :href="url" class="p-h01-point-confirm-item">
      <div class="p-h01-point-confirm-item__content">
        <div class="p-h01-point-confirm-item__meta">
          <span
            v-if="status"
            class="p-h01-point-confirm-item__status"
            :class="`p-h01-point-confirm-item__status--${usage}`"
          >
            {{ status }}
          </span>
          <span v-if="date" class="p-h01-point-confirm-item__date">
            {{ date }}
          </span>
        </div>

        <div class="p-h01-point-confirm-item__detail">
          <div class="p-h01-point-confirm-item__icon">
            <IconInvestedRounded
              v-if="iconType === 1"
              :type="iconPath"
              :icon-size="24"
              :bg-color="colorCode"
              icon-color="#ffffff"
            />
            <IconUserImage v-else :size="24" :unread="unread">
              <img
                v-if="iconPath !== false && iconPath !== ''"
                :src="iconPath"
                alt
              />
            </IconUserImage>
          </div>

          <div class="p-h01-point-confirm-item__description">
            <span
              v-if="group"
              class="p-h01-point-confirm-item__group"
              data-translate-skip
            >
              {{ group }}
            </span>
            <span
              v-if="name"
              class="p-h01-point-confirm-item__name"
              :data-translate-skip="status !== '利用' ? '' : null"
            >
              {{ name }}
            </span>
          </div>
        </div>
      </div>

      <div class="p-h01-point-confirm-item__aside">
        <div class="p-h01-point-confirm-item__value">
          <div
            class="p-h01-point-confirm-item__labels"
            :class="{ '-empty': !hasLabels }"
          >
            <TextLabel
              v-if="approvedEnabled && approved"
              :color="{ border: '#777777', content: '#ffffff' }"
            >
              {{ approved }}
            </TextLabel>
            <TextLabel
              v-if="label"
              :color="{ border: colorCode, content: '#ffffff' }"
              :reverse="reverseLabelColor"
            >
              {{ label }}
            </TextLabel>
          </div>

          <div
            class="p-h01-point-confirm-item__point"
            :class="
              sign
                ? 'p-h01-point-confirm-item__point--plus'
                : 'p-h01-point-confirm-item__point--minus'
            "
          >
            <span class="p-h01-point-confirm-item__point__sign">
              {{ sign ? "+" : "−" }}
            </span>
            <PointNotation
              :icon-size="false"
              :point="point"
              :point-color="pointColor"
              :sign-is-exists="false"
              :font-size="pointNotationStyle.fontSize"
            />
          </div>
        </div>

        <IconBase
          class="p-h01-point-confirm-item__arrow"
          :width="24"
          :height="24"
          view-box="0 0 24 24"
        >
          <IconArrowRight />
        </IconBase>
      </div>
    </a>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";

import IconUserImage from "@/common/components/icons/IconUserImage.vue";
import PointNotation from "@/common/components/atoms/PointNotation.vue";
import TextLabel from "@/common/components/atoms/TextLabel.vue";
import IconInvestedRounded from "@/common/components/icons/IconInvestedRounded.vue";
import IconBase from "@/common/components/atoms/IconBase.vue";
import IconArrowRight from "@/common/components/icons/IconArrowRight.vue";

import commons from "@/common/store/modules/commons";
import { mapState } from "vuex";

@Options({
  props: {
    iconPath: { type: [String, Boolean], default: false },
    unread: { type: Boolean, default: false },
    name: { type: [String, Boolean], default: false },
    date: { type: String },
    status: { type: [String, Boolean], default: false },
    usage: { type: String, required: true },
    label: { type: [String, Boolean], default: false },
    labelType: { type: [String, Boolean], default: false },
    group: { type: [String, Boolean], default: false },
    point: { type: [Number, Boolean], default: false },
    sign: { type: Boolean, default: false },
    url: { type: String, required: true },
    approved: { type: [String, Boolean], default: false },
    approvedEnabled: { type: Boolean, default: false },
    iconType: { type: [Number, Boolean], default: false }
  },
  components: {
    IconUserImage,
    PointNotation,
    TextLabel,
    IconInvestedRounded,
    IconBase,
    IconArrowRight
  },
  computed: {
    ...mapState({
      storeColorCode: () => commons.colorCode
    })
  }
})
export default class H01PointConfirmItem extends Vue {
  iconPath!: string | boolean;
  unread!: boolean;
  name!: string | boolean;
  date!: string;
  status!: string | boolean;
  usage!: string;
  label!: string | boolean;
  labelType!: string | boolean;
  group!: string | boolean;
  point!: number | boolean;

  sign!: boolean;
  url!: string;
  approved!: string | boolean;
  approvedEnabled!: boolean;
  iconType!: number | boolean;
  storeColorCode: string;

  get hasLabels() {
    return Boolean((this.approvedEnabled && this.approved) || this.label);
  }

  get isGroupBudget() {
    return commons?.userInfo.groupBudgetMode;
  }

  get colorCode() {
    if (this.isGroupBudget) {
      return "#fc9014";
    }
    return this.storeColorCode;
  }

  get reverseLabelColor() {
    const { labelType } = this;
    let reverse = false;

    switch (labelType) {
      case "thanks":
        reverse = true;
        break;

      case "distribution":
        reverse = false;
        break;
    }

    return reverse;
  }

  get pointNotationStyle() {
    return {
      fontSize: 18
    };
  }

  get pointColor() {
    return this.sign ? "#da291c" : "#0d76bf";
  }
}
</script>

<style lang="scss" scoped>
.p-h01-point-confirm-item {
  display: flex;
  align-items: center;
  gap: rem(16);
  padding: rem(12);

  @include large-sp {
    gap: rem(8);
  }

  &__content {
    display: flex;
    align-items: center;
    gap: rem(16);
    min-width: 0;
    flex: 1;

    @include large-sp {
      flex-direction: column;
      align-items: stretch;
      gap: rem(8);
    }
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: rem(16);
    flex-shrink: 0;

    @include large-sp {
      gap: rem(8);
    }
  }

  &__status {
    padding: rem(8);
    color: $common-white;
    font-family: "Noto Sans JP";
    font-size: rem(14);
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
    background-color: #ff0000;

    @include large-sp {
      padding: rem(4);
    }

    @include sp {
      font-size: rem(12);
    }

    &--invested {
      background-color: #ff0000;
    }

    &--gived {
      background-color: #ff5050;
    }

    &--give {
      background-color: #2f5597;
    }

    &--user {
      background-color: #5b9bd5;
    }
  }

  &__date {
    color: $common-black;
    font-family: "Noto Sans JP";
    font-size: rem(14);
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;

    @include sp {
      font-size: rem(12);
    }
  }

  &__labels {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: rem(4);
    width: rem(88);
    flex-shrink: 0;

    &.-empty {
      @include sp {
        display: none;
      }
    }

    :deep(.c-text-label) {
      box-sizing: border-box;
      width: rem(88);
      overflow-wrap: anywhere;
      white-space: normal;
    }

    :deep(.c-text-label__text) {
      display: inline;
      overflow-wrap: anywhere;
    }
  }

  &__aside {
    display: flex;
    align-items: center;
    gap: rem(16);
    flex-shrink: 0;

    @include large-sp {
      gap: rem(8);
    }
  }

  &__value {
    display: flex;
    align-items: center;
    gap: rem(16);
    min-width: 0;

    @include large-sp {
      gap: rem(8);
    }

    @include sp {
      flex-direction: column;
      align-items: flex-end;
      gap: rem(4);
    }
  }

  &__detail {
    display: flex;
    align-items: center;
    gap: rem(8);
    min-width: 0;
    flex: 1;
  }

  &__icon {
    width: rem(24);
    min-width: rem(24);
  }

  &__description {
    display: flex;
    flex-direction: column;
    gap: rem(2);
    min-width: 0;
  }

  &__group {
    color: $common-black;
    font-family: "Noto Sans JP";
    font-size: rem(12);
    font-weight: 400;
    line-height: 1.25;
    overflow-wrap: anywhere;

    @include sp {
      font-size: rem(10);
    }
  }

  &__name {
    color: $common-black;
    font-family: "Noto Sans JP";
    font-size: rem(14);
    font-weight: 700;
    line-height: 1.5;
    overflow-wrap: anywhere;
    white-space: normal;

    @include sp {
      font-size: rem(12);
    }
  }

  &__point {
    display: flex;
    align-items: center;
    gap: rem(4);
    justify-content: flex-end;
    flex-shrink: 0;
    white-space: nowrap;

    @include pc {
      min-width: rem(170);
    }

    &--plus {
      color: #da291c;
    }

    &--minus {
      color: #0d76bf;
    }

    &__sign {
      font-family: "Noto Sans JP";
      font-size: rem(16);
      font-weight: 700;
      line-height: 1;
    }

    :deep(.p-point-notation__point__unit) {
      color: inherit;
    }

    @include sp {
      :deep(.p-point-notation),
      :deep(.p-point-notation__point__num) {
        font-size: rem(16) !important;
      }

      :deep(.p-point-notation__point__unit) {
        font-size: rem(10) !important;
      }
    }
  }

  &__arrow {
    flex-shrink: 0;
  }
}
</style>
